<template>
  <v-container fluid>
    <UniversalTable
      :config="tableConfig.tableParams.value"
      :items="displayItems"
      :items-count="displayItemsCount"
      backend-pagination
      :externalCurrentPage="Math.floor(paginationDefaultParams.offset / paginationDefaultParams.limit) + 1"
      @onSearch="viewState.handleSearch"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @removeFilter="viewState.removeFilters"
      @removeAllFilters="viewState.clearFilters"
      @onSort="viewState.handleSort"
      @onRequest="viewState.handleRequest"
      @onActionClick="handleIssueBadge"
      @levelCounterFilter="handleLevelCounterFilter"
      :loading="$loader.isAppLoading"
      :emptyObjectData="{
        singular: 'Badge Holder',
        plural: 'Badge Holders',
        emptyTitle: 'No Badges where issued for this Responsibility',
        emptyMessage: 'You need at least a Master Badge and some Professionals to fulfill this Responsibility',
        emptyButtonLabel: 'Issue Badge'
      }"
    >
    </UniversalTable>

    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="viewState.applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Badge Holders"
      :groupsItems="viewState.state.filters.groupsItems"
      :filterGroups="viewState.state.filters.filterGroups"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="620"
      :defaultSelected="viewState.state.filters.values"
    />
  </v-container>
</template>

<script setup>
import { defineProps, defineEmits, computed, watch, toRef } from "vue";
import { useBadgeHolders } from "./composables/useBadgeHolders";
import { useTableConfig } from "./composables/useTableConfig";
import { useBadgeHoldersViewState } from "./composables/useBadgeHoldersViewState";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";

const props = defineProps({
  responsibilityId: {
    type: [Number, String],
    required: true,
  },
  items: {
    type: Array,
    required: true,
    description: "Array of badge holder items to display",
  },
  itemsCount: {
    type: Number,
    default: 0,
    description: "Total count of items for pagination",
  },
  filterLevels: {
    type: Array,
    default: () => [],
    description: "Default filter levels to apply (e.g., ['Coach'] or ['Master'])",
  },
  badgeLevelCounts: {
    type: Object,
    default: () => ({
      apprentice: 0,
      professional: 0,
      coach: 0,
      master: 0,
    }),
    description: "Badge level counts from responsibilityData",
  },
});

const emit = defineEmits(['removeBadge', 'filtersChanged', 'onActionClick']);

const badgeHolders = useBadgeHolders();
const viewState = useBadgeHoldersViewState();
const paginationDefaultParams = badgeHolders.paginationDefaultParams;

badgeHolders.allBadgeHolders.value = props.items;
badgeHolders.badgeHoldersCount.value = props.itemsCount || props.items.length;

watch(
  () => viewState.state.filters.values,
  (newFilters) => {
    const statusFilters = newFilters.filter(filter => 
      ['Current', 'Expired', 'Terminated', 'Removed'].includes(filter)
    );
    
    const levelFilters = newFilters.filter(filter => 
      ['Apprentice', 'Professional', 'Coach', 'Master'].includes(filter)
    );
    
    const levelNumbers = levelFilters.map(level => {
      switch(level) {
        case 'Apprentice':
          return 1;
        case 'Professional':
          return 2;
        case 'Coach':
          return 3;
        case 'Master':
          return 4;
        default:
          return null;
      }
    }).filter(Boolean);
    
    emit('filtersChanged', {
      status: statusFilters,
      level: levelNumbers,
    });
  },
  { immediate: true }
);

const handleRemoveBadge = (item) => {
  emit('removeBadge', item);
};

const handleIssueBadge = () => {
  emit('onActionClick');
};

const handleLevelCounterFilter = (selectedLevels) => {
  const currentFilters = [...viewState.state.filters.values];
  const statusFilters = currentFilters.filter(filter => 
    ['Current', 'Expired', 'Terminated', 'Removed'].includes(filter)
  );
  
  const levelNumbers = selectedLevels.map(level => {
    switch(level) {
      case 'Apprentice':
        return 1;
      case 'Professional':
        return 2;
      case 'Coach':
        return 3;
      case 'Master':
        return 4;
      default:
        return null;
    }
  }).filter(Boolean);
  
  emit('filtersChanged', {
    status: statusFilters,
    level: levelNumbers,
  });
};

const badgeLevelCountsRef = toRef(props, 'badgeLevelCounts');
const tableConfig = useTableConfig(props.filterLevels, handleRemoveBadge, badgeLevelCountsRef);

const displayItems = computed(() => props.items);
const displayItemsCount = computed(() => props.itemsCount || props.items.length);
</script>

<style scoped>
::v-deep .v-list-item.with-employee {
  border: none !important;
  min-width: 250px !important;
  max-width: 290px !important;
}

::v-deep .empty-content-title,
::v-deep .empty-content-description {
    max-width: 352px !important;
}

::v-deep .table-content.empty-background-pattern {
    background-position: 50% -46px !important;
}

::v-deep .user-card .el-tooltip div {
  height: 25px !important;
}
</style>
